import { AllWelcomePacks_allSignupPromoCodes as WelcomePack } from "../types/__generated__/AllWelcomePacks";
import { WINE_CLASS_IDS } from "./constants";
import { COUNTRY_INFO_BY_CODE, getCountry } from "./urls.helper";

export type GuestSubscriptionDestination = "FIRST_SUBSCRIPTION" | "EXISTING_SUBSCRIPTION" | "ONE_OFF_CART";

export interface GuestWelcomePackChoice {
  countryId: number;
  packId: number;
  promoCode: string;
  preferredWineClassId?: number;
  label: string;
}

// Target bottle count per wine colour for the first box (the guest "wine quantities" step).
// Absolute counts, never increments: the backend fills any colour still short of its target
// with recommended wines after the cart's own wines are routed.
export interface GuestBoxWineQuantity {
  wineClassId: number;
  numberOfBottles: number;
}

export interface GuestSubscriptionCheckout {
  countryId: number;
  choice: GuestWelcomePackChoice | null;
  boxWineQuantities: GuestBoxWineQuantity[] | null;
  memberId: number | null;
  destination: GuestSubscriptionDestination | null;
  transferComplete: boolean;
  needsSelection: boolean;
}

const STORAGE_KEY = "guestSubscriptionCheckout";
export const GUEST_SUBSCRIPTION_TRANSFER_KEY = "guestSubscriptionTransfer";
const CHANGE_EVENT = "guestSubscriptionCheckoutChange";
// Colours a guest can choose for their box: sake and dessert wines have no recurring
// wine preference on the backend, so neither a welcome wine nor a box target can use them.
export const GUEST_BOX_WINE_CLASS_IDS = [
  WINE_CLASS_IDS.DB_ID_WINE_CLASS_RED,
  WINE_CLASS_IDS.DB_ID_WINE_CLASS_WHITE,
  WINE_CLASS_IDS.DB_ID_WINE_CLASS_ROSE,
  WINE_CLASS_IDS.DB_ID_WINE_CLASS_SPARKLING,
];

export const isValidGuestBoxWineQuantities = (value: unknown): value is GuestBoxWineQuantity[] => {
  if (!Array.isArray(value)) return false;
  const wineClassIds = new Set<number>();
  return value.every((entry) => {
    const wineClassId = entry?.wineClassId;
    const numberOfBottles = entry?.numberOfBottles;
    if (!GUEST_BOX_WINE_CLASS_IDS.includes(wineClassId) || wineClassIds.has(wineClassId)) return false;
    wineClassIds.add(wineClassId);
    return Number.isInteger(numberOfBottles) && numberOfBottles >= 0;
  });
};

export const getGuestCheckoutCountryId = (): number =>
  COUNTRY_INFO_BY_CODE[getCountry() === "uk" ? "uk" : "au"].countryId;

export const clearGuestSubscriptionCheckout = () => {
  window.localStorage.removeItem(STORAGE_KEY);
  window.localStorage.removeItem(GUEST_SUBSCRIPTION_TRANSFER_KEY);
  window.dispatchEvent(new Event(CHANGE_EVENT));
};

export const getGuestSubscriptionCheckout = (
  countryId = getGuestCheckoutCountryId()
): GuestSubscriptionCheckout | null => {
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (!stored) return null;
  try {
    const value: GuestSubscriptionCheckout = JSON.parse(stored);
    if (
      value?.countryId !== countryId ||
      typeof value.transferComplete !== "boolean" ||
      typeof value.needsSelection !== "boolean" ||
      (value.boxWineQuantities != null && !isValidGuestBoxWineQuantities(value.boxWineQuantities)) ||
      (value.memberId !== null && !Number.isInteger(value.memberId)) ||
      ![null, "FIRST_SUBSCRIPTION", "EXISTING_SUBSCRIPTION", "ONE_OFF_CART"].includes(value.destination) ||
      (value.choice !== null &&
        (value.choice?.countryId !== countryId ||
          !Number.isInteger(value.choice?.packId) ||
          typeof value.choice?.promoCode !== "string" ||
          !value.choice.promoCode ||
          typeof value.choice?.label !== "string"))
    ) {
      window.localStorage.removeItem(STORAGE_KEY);
      window.localStorage.removeItem(GUEST_SUBSCRIPTION_TRANSFER_KEY);
      return null;
    }
    return { ...value, boxWineQuantities: value.boxWineQuantities ?? null };
  } catch {
    window.localStorage.removeItem(STORAGE_KEY);
    window.localStorage.removeItem(GUEST_SUBSCRIPTION_TRANSFER_KEY);
    return null;
  }
};

const saveCheckout = (checkout: GuestSubscriptionCheckout) => {
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(checkout));
  window.dispatchEvent(new Event(CHANGE_EVENT));
  return checkout;
};

export const beginGuestSubscriptionCheckout = (countryId = getGuestCheckoutCountryId()) =>
  getGuestSubscriptionCheckout(countryId) ??
  saveCheckout({
    countryId,
    choice: null,
    boxWineQuantities: null,
    memberId: null,
    destination: null,
    transferComplete: false,
    needsSelection: false,
  });

export const updateGuestSubscriptionCheckout = (
  patch: Partial<Omit<GuestSubscriptionCheckout, "countryId">>
) => saveCheckout({ ...beginGuestSubscriptionCheckout(), ...patch });

export const setGuestWelcomePackChoice = (choice: GuestWelcomePackChoice) => {
  if (choice.countryId !== getGuestCheckoutCountryId()) return null;
  return updateGuestSubscriptionCheckout({ choice, needsSelection: false });
};

export const clearGuestWelcomePackChoice = () => {
  const checkout = getGuestSubscriptionCheckout();
  if (checkout) saveCheckout({ ...checkout, choice: null });
};

export const setGuestBoxWineQuantities = (quantities: GuestBoxWineQuantity[]) => {
  const boxWineQuantities = quantities.filter((quantity) => quantity.numberOfBottles > 0);
  if (!isValidGuestBoxWineQuantities(boxWineQuantities)) return null;
  return updateGuestSubscriptionCheckout({ boxWineQuantities });
};

export const clearGuestBoxWineQuantities = () => {
  const checkout = getGuestSubscriptionCheckout();
  if (checkout?.boxWineQuantities) saveCheckout({ ...checkout, boxWineQuantities: null });
};

export const onGuestSubscriptionCheckoutChange = (listener: () => void) => {
  const onStorage = (event: StorageEvent) => {
    if (!event.key || event.key === STORAGE_KEY) listener();
  };
  window.addEventListener(CHANGE_EVENT, listener);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(CHANGE_EVENT, listener);
    window.removeEventListener("storage", onStorage);
  };
};

export const isGuestWelcomePackSelectable = (pack: WelcomePack | null): pack is WelcomePack =>
  !!pack && !pack.isSoldOut && Number.isInteger(pack.id) && !!pack.promoCode?.name;

export const isValidGuestWelcomePackChoice = (
  choice: GuestWelcomePackChoice | null | undefined,
  packs: readonly (WelcomePack | null)[],
  countryId = getGuestCheckoutCountryId()
): boolean => {
  if (!choice || choice.countryId !== countryId) return false;
  const pack = packs.find(
    (candidate) => candidate?.id === choice.packId && candidate?.promoCode?.name === choice.promoCode
  );
  if (!pack || !isGuestWelcomePackSelectable(pack)) return false;
  return (
    !pack.promoCode?.isFreeWineForAYearPromo ||
    GUEST_BOX_WINE_CLASS_IDS.includes(choice.preferredWineClassId ?? 0)
  );
};
